import _ from 'lodash'
import { store } from '@atredux/store'
import {
    getStoreId,
    getStoreInfo,
} from '@atredux/selectors/storeSelector'
import {
    setStoreId,
    setStoreInfo,
} from '@atredux/slices/storeSlice'
import { generateDeferredPromise } from '@utils/DeferredPomiseUtil'
import { AtCoreServiceClient } from '@api/AtCoreServiceClient'

import * as FeatureConfigService from '@services/FeatureConfigService'
import { DorothyUtils } from '@utils/DorothyUtils'
import { getStoreInformation } from '@api/userAndStoreContext'
import { devLog } from '@src/config/localDevConfig'

export const enum StoreFamily {
    IHM = 'IHM',
    JANE = 'JANE',
    MENDEL = 'MENDEL',
    WFM = 'WFM',
    HOUDINI = 'HOUDINI',
    KISAN = 'KISAN',
    KASPIAN = 'KASPIAN',
    DOROTHY = 'DOROTHY',
    UV = 'UV',
    ASSET = 'ASSET',
    SSD = 'SSD',
}

// Note: Libby Tags are NOT allowed to have hyphens or underscores
export const enum IhmFacilityTags {
    'KITCHEN_OFFSITE' = 'OFFSITEKITCHEN',
    'KITCHEN_ONSITE' = 'ONSITEKITCHEN',
}

export const setCurrentStore = async (storeId: string): Promise<string> => {
    console.log(`Setting store info for ${storeId}`)

    const storeData = await getStoreInformation(storeId)
    const storeDetails = storeData.storeDetails

    const enrichedStoreInfo = {
        ...storeDetails,
        storeFamily: deriveStoreFamilyFromTags(storeDetails.tags || []),
        isKitchen: deriveIsKitchenFromTags(storeDetails.tags || []),
        isDorothyEnabled: deriveIsDorothyEnabledFromTags(storeDetails.tags || []),
    }

    devLog('Store tags for', storeId, ':', storeDetails.tags)
    devLog('Derived storeFamily:', enrichedStoreInfo.storeFamily)
    store.dispatch(setStoreId(storeId))
    store.dispatch(setStoreInfo(enrichedStoreInfo))
    window.sessionStorage.setItem('storeFamily', enrichedStoreInfo.storeFamily || '')

    return storeId
}

function deriveStoreFamilyFromTags (tags: string[]): StoreFamily | undefined {
    if (tags.includes('ASSET')) return StoreFamily.ASSET
    if (tags.includes('IHM')) return StoreFamily.IHM
    if (tags.includes('JANE')) return StoreFamily.JANE
    if (tags.includes('WFM')) return StoreFamily.WFM
    if (tags.includes('MENDEL')) return StoreFamily.MENDEL
    if (tags.includes('HOUDINI')) return StoreFamily.HOUDINI
    if (tags.includes('KISAN')) return StoreFamily.KISAN
    if (tags.includes('KASPIAN')) return StoreFamily.KASPIAN
    if (tags.includes('DOROTHY')) return StoreFamily.DOROTHY
    if (tags.includes('SSD')) return StoreFamily.SSD
    if (tags.includes('UV')) return StoreFamily.UV
    return undefined
}

function deriveIsKitchenFromTags (tags: string[]): boolean {
    return tags.includes(IhmFacilityTags.KITCHEN_OFFSITE) || tags.includes(IhmFacilityTags.KITCHEN_ONSITE)
}

function deriveIsDorothyEnabledFromTags (tags: string[]): boolean {
    return tags.includes('DOROTHY')
}

export const isIhmStore = () => {
    return getStoreInfo(store.getState())?.storeFamily === StoreFamily.IHM &&
    !getStoreInfo(store.getState())?.isKitchen
}
export const isJaneStore = () => getStoreInfo(store.getState())?.storeFamily === StoreFamily.JANE
export const isWFMStore = () => getStoreInfo(store.getState())?.storeFamily === StoreFamily.WFM
export const isMendelStore = () => getStoreInfo(store.getState())?.storeFamily === StoreFamily.MENDEL
export const isAssetStore = () => getStoreInfo(store.getState())?.storeFamily === StoreFamily.ASSET
export const isKisanStore = () => getStoreInfo(store.getState())?.storeFamily === StoreFamily.KISAN
export const isHoudiniStore = () => getStoreInfo(store.getState())?.storeFamily === StoreFamily.HOUDINI
export const isDorothyStore = () => getStoreInfo(store.getState())?.storeFamily === StoreFamily.DOROTHY
export const isKaspianStore = () => getStoreInfo(store.getState())?.storeFamily === StoreFamily.KASPIAN
export const isSSDStore = () => getStoreInfo(store.getState())?.storeFamily === StoreFamily.SSD
export const isKitchenStore = () => getStoreInfo(store.getState())?.isKitchen
export const isWfmStoresNotificationsEnabled = () => (
    isWFMStore() &&
    FeatureConfigService.isFeatureEnabled(
        FeatureConfigService.featureConfigFeatures.ENABLE_WFM_STORES_NOTIFICATIONS,
        false,
    )
)

// TODO: Complete function when capability service migrated
// TODO: determine whether SSD is notifiable
export const isNotifiable = () => (
    isIhmStore() ||
    isJaneStore() ||
    isMendelStore() ||
    isAssetStore() ||
    isWfmStoresNotificationsEnabled() ||
    isKisanStore() ||
    isHoudiniStore() ||
    isDorothyStore())
